import { environment } from '../../../../environments/environment';
import { ProjectEnvironment, ProjectProfile } from '../../../models/projects.model';

export interface AppSettingsResponse {
  tenant?: string;
  environment?: string;
  tenantDisplayName?: string;
  logoBase64?: string;
  compactLogoBase64?: string;
  faviconUrl?: string;
  properties?: {
    favicon?: string;
    name?: string;
    abbreviation?: string;
    phone?: string;
    fax?: string;
    email?: string;
    address_line_one?: string;
    address_line_two?: string;
    url?: string;
    supportAbbreviation?: string;
  };
  config?: ConfigFeatures;
}

export interface AppLogoResponse {
  tenantDisplayName?: string;
  logoBase64?: string;
  compactLogoBase64?: string;
  faviconUrl?: string;
  properties?: {
    favicon?: string;
    name?: string;
    abbreviation?: string;
    phone?: string;
    fax?: string;
    email?: string;
    address_line_one?: string;
    address_line_two?: string;
    url?: string;
    supportAbbreviation?: string;
  };
}

export interface AccessRulesState {
  profile: ProjectProfile;
  environment: ProjectEnvironment;
  projectName: string;
  logoBase64: string;
  compactLogoBase64: string;
  faviconUrl: string;
  supportAbbreviation: string;
  features: ConfigFeatures;
  projectInfo: ProjectInfo;
}

export interface ConfigFeatures {
  fax: FaxConfig;
  caseAppeals: FeatureConfig;
  clientManagement: FeatureConfig;
  mailingLabels: FeatureConfig;
  matomoSiteId: number;
  sides: SidesFeatureConfig;
  reasonCodes: FeatureConfig;
  hearings: FeatureConfig;
  managedRules: FeatureConfig;
  states: FeatureConfig;
  plextera: PlexteraFeatureConfig;
  analystPerformance: FeatureConfig;
  activeUsersStatistics: FeatureConfig;
  partnerProgram: FeatureConfig;
  aboutPage: FeatureConfig;
  rateNotices: FeatureConfig;
  statementsOfAccount: FeatureConfig;
  protest: FeatureConfig;
  actionItems: ActionItemsFeatureConfig;
  supportWidget: SupportWidgetFeatureConfig;
  plexteraDocumentInfo: PlexteraDocumentInfoFeatureConfig;
  benefitCharges: BenefitCharges;
  integrationHub: FeatureConfig;
  menuItems: MenuItemsConfig;
  twoFactorAuthentication: TwoFactorAuthenticationConfig;
  letterTemplate: FeatureConfig;
}

export interface FaxConfig {
  autoProtestEnabled: boolean;
  documentFaxEnabled: boolean;
  faxSendDelaySeconds: number;
  maxAutoRetryCount: number;
  faxFromNumber: string;
}
export interface FeatureConfig {
  enable: boolean;
}

export interface CaseAppeals {
  determinationAppeal: FeatureConfig;
  boardOfReview: FeatureConfig;
}

export interface BenefitCharges {
  enable: boolean;
  reportVisibility: ReportVisibility;
  settings: BenefitChargesSettings;
}

export interface IntegrationHub {
  enable: boolean;
  scheduler: IntegrationHubScheduler;
}
export interface IntegrationHubScheduler {
  bc: FeatureConfig;
  dd: FeatureConfig;
  md: FeatureConfig;
  rn: FeatureConfig;
  si: FeatureConfig;
  nmd: FeatureConfig;
  soa: FeatureConfig;
  claimantSyncUp: FeatureConfig;
}
export interface ReportVisibility {
  chargeHistoryReport: boolean;
  reimbursableBillsList: boolean;
  fraudulentClaimCharges: boolean;
  reimbursableBillsCopies: boolean;
  covidChargesAndCredits: boolean;
  protestableBCPerStateProgram: boolean;
  protestFactFindingTimeliness: boolean;
  protestableBenefitChargesReport: boolean;
  missingBenefitChargeStatementsReport: boolean;
  remainingLiability: boolean;
  uiActivityReport: boolean;
  annualPages: boolean;
}

export interface BenefitChargesSettings {
  createAiOnMissingCase: boolean;
}

export interface TwoFactorAuthenticationConfig {
  pin?: {
    enable: boolean;
  };

  totp?: {
    enable: boolean;
  };
}

export interface MenuItemsConfig extends FeatureConfig {
  enable: boolean;
  properties?: {
    companies?: string;
    personnel?: string;
    clientDocuments?: string;
    clientReports?: string;
    contractsAndRenewals?: string;
    invoices?: string;
    invoicesProcessInvoices?: string;
    invoicesInvoicesToPull?: string;
    invoicesCreditInvoices?: string;
    invoicesInvoiceStatus?: string;
    statements?: string;
    statementsProcessStatements?: string;
    statementsStatementsToPull?: string;
    statementsStatementStatus?: string;
    payments?: string;
    paymentsEnterPayments?: string;
    paymentsAdjustAccounts?: string;
    billingReports?: string;
    dashboard?: string;
    contacts?: string;
    cases?: string;
    sides?: string;
    sidesConnectors?: string;
    sidesSearch?: string;
    benefitCharges?: string;
    protests?: string;
    statementsOfAccount?: string;
    rateNotices?: string;
    documents?: string;
    activities?: string;
    activitiesBenefitCharges?: string;
    activitiesPartnerProgramMonthlyEmail?: string;
    activitiesRateNotices?: string;
    activitiesMissingRateNotices?: string;
    activitiesEmailNotification?: string;
    activitiesMaRateNotices?: string;
    activitiesMailingLabels?: string;
    reports?: string;
    reportsUiActivityReport?: string;
    reportsRateNotices?: string;
    reportsBenefitCharges?: string;
    reportsPartnerPrograms?: string;
    reportsAnnualPages?: string;
    billing?: string;
    analytics?: string;
    analyticsAnalystPerformance?: string;
    settings?: string;
    settingsData?: string;
    settingsDataStates?: string;
    settingsDataReasonCodes?: string;
    settingsDataHearings?: string;
    settingsDataManagedRules?: string;
    settingsInternalUsers?: string;
    settingsAnalystsAssigned?: string;
    settingsPartnerPrograms?: string;
    settingsAuthorizeAccess?: string;
    tools?: string;
    toolsOcr?: string;
    toolsFaxHistory?: string;

    help?: string;
    about?: string;
  };
}

export interface SupportWidgetFeatureConfig extends FeatureConfig {
  properties: {
    WIDGET_ID: string;
    WIDGET_URL: string;
  };
}

export interface PlexteraDocumentInfoFeatureConfig extends FeatureConfig {
  properties: {
    WIDGET_KEY: string;
  };
}

export interface ActionItemsFeatureConfig {
  summaryWarningLabels: FeatureConfig;
  sourceTypes: {
    uiOnline: FeatureConfig;
    mpc: FeatureConfig;
  };
}

export interface SidesFeatureConfig extends FeatureConfig {
  exchanges: {
    si: FeatureConfig;
    mpc: FeatureConfig;
    dd: FeatureConfig;
    adf: FeatureConfig;
    bc: FeatureConfig;
    ev: FeatureConfig;
  };
}

export interface PlexteraFeatureConfig {
  integrations: {
    sso: {
      enable: boolean;
      PLEXTERA_LOGIN_SSO_URL: string;
      PLEXTERA_LOGIN_SSO_ORG_ID: string;
      PLEXTERA_LOGIN_SSO_SECRET: string;
    };
    ui21a: FeatureConfig;
  };
}

export interface ProjectInfo {
  name: string;
  abbreviation: string;
  phone: string;
  fax: string;
  email: string;
  address_line_one: string;
  address_line_two: string;
  url: string;
}

export const defaultConfig: ConfigFeatures = {
  clientManagement: { enable: false },
  mailingLabels: { enable: false },
  caseAppeals: {
    enable: false,
  },
  letterTemplate: { enable: false },
  fax: {
    autoProtestEnabled: false,
    documentFaxEnabled: true,
    faxSendDelaySeconds: 0,
    maxAutoRetryCount: 0,
    faxFromNumber: '',
  },
  sides: {
    enable: false,
    exchanges: {
      si: {
        enable: false,
      },
      mpc: {
        enable: false,
      },
      dd: {
        enable: false,
      },
      adf: {
        enable: false,
      },
      bc: {
        enable: false,
      },
      ev: {
        enable: false,
      },
    },
  },
  matomoSiteId: 8,
  reasonCodes: { enable: false },
  hearings: { enable: false },
  managedRules: { enable: true },
  states: { enable: false },
  plextera: {
    integrations: {
      sso: {
        enable: false,
        PLEXTERA_LOGIN_SSO_URL: '12345',
        PLEXTERA_LOGIN_SSO_ORG_ID: '12354',
        PLEXTERA_LOGIN_SSO_SECRET: '12345',
      },
      ui21a: {
        enable: false,
      },
    },
  },
  analystPerformance: { enable: false },
  activeUsersStatistics: { enable: false },
  partnerProgram: { enable: false },
  aboutPage: { enable: false },
  rateNotices: { enable: false },

  statementsOfAccount: { enable: false },
  protest: { enable: false },
  actionItems: {
    summaryWarningLabels: {
      enable: false,
    },
    sourceTypes: {
      uiOnline: {
        enable: false,
      },
      mpc: {
        enable: false,
      },
    },
  },
  supportWidget: {
    enable: false,
    properties: {
      WIDGET_ID: '0807af02-0aa5-42b7-b2d2-11571759624c',
      WIDGET_URL: 'https://plextera.youtrack.cloud',
    },
  },
  plexteraDocumentInfo: {
    enable: true,
    properties: {
      WIDGET_KEY: environment.docInsightsWidgetKey,
    },
  },
  benefitCharges: {
    enable: true,
    reportVisibility: {
      chargeHistoryReport: true,
      reimbursableBillsList: true,
      fraudulentClaimCharges: true,
      reimbursableBillsCopies: true,
      covidChargesAndCredits: true,
      protestableBCPerStateProgram: true,
      protestFactFindingTimeliness: true,
      protestableBenefitChargesReport: true,
      missingBenefitChargeStatementsReport: true,
      remainingLiability: true,
      uiActivityReport: true,
      annualPages: true,
    },
    settings: {
      createAiOnMissingCase: true,
    },
  },
  integrationHub: {
    enable: false,
  },
  twoFactorAuthentication: {
    pin: { enable: false },
  },
  menuItems: {
    enable: false,
    properties: {
      companies: 'Companies',
      clientDocuments: 'Client Documents',
      clientReports: 'Client Reports',
      contractsAndRenewals: 'Contracts & Renewals',
      invoices: 'Invoices',
      invoicesProcessInvoices: 'Process Invoices',
      invoicesInvoicesToPull: 'Invoices to Pull',
      invoicesCreditInvoices: 'Credit Invoices',
      invoicesInvoiceStatus: 'Invoice Status',
      statements: 'Statements',
      statementsProcessStatements: 'Process Statements',
      statementsStatementsToPull: 'Statements to Pull',
      statementsStatementStatus: 'Statement Status',
      payments: 'Payments',
      paymentsEnterPayments: 'Enter Payments',
      paymentsAdjustAccounts: 'Adjust Accounts',
      billingReports: 'Billing Reports',
      dashboard: 'dashboard',
      contacts: 'Contacts',
      cases: 'Cases',
      sides: 'SIDES',
      sidesConnectors: 'Connectors',
      sidesSearch: 'Search',
      benefitCharges: 'Benefit Charges',
      protests: 'Protests',
      statementsOfAccount: 'Statements of Account',
      rateNotices: 'Rate Notices',
      documents: 'Documents',
      activities: 'Activities',
      activitiesBenefitCharges: 'Benefit Charges',
      activitiesPartnerProgramMonthlyEmail: 'Partner Program Monthly Email',
      activitiesRateNotices: 'Rate Notices',
      activitiesMissingRateNotices: 'Missing Rate Notices',
      activitiesEmailNotification: 'Email Notification',
      activitiesMaRateNotices: 'MA Rate Notices',
      activitiesMailingLabels: 'Mailing Labels',
      reports: 'Reports',
      reportsUiActivityReport: 'UI Activity Report',
      reportsRateNotices: 'Rate Notices',
      reportsBenefitCharges: 'Benefit Charges',
      reportsPartnerPrograms: 'Partner Programs',
      reportsAnnualPages: 'Annual Pages',
      billing: 'Billing',
      analytics: 'Analytics',
      analyticsAnalystPerformance: 'Analyst Performance',
      settings: 'Settings',
      settingsData: 'Data',
      settingsDataStates: 'States',
      settingsDataReasonCodes: 'Reason Codes',
      settingsDataHearings: 'Hearings',
      settingsDataManagedRules: 'Managed Rules',
      settingsInternalUsers: 'Internal Users',
      settingsAnalystsAssigned: 'Analysts Assigned',
      settingsPartnerPrograms: 'Partner Programs',
      settingsAuthorizeAccess: 'Authorize Access',
      tools: 'Tools',
      toolsOcr: 'OCR Gateway',
      help: 'Help',
      about: 'About',
    },
  },
};
